import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Card, Space, Typography, Badge } from 'antd';
import { EnvironmentOutlined, AimOutlined, LoadingOutlined } from '@ant-design/icons';
import { showErrorNotification } from '@/components/common/utils/notify';
import { useTheme } from '@/components/common/ThemeProvider';

const { Text } = Typography;

export interface GPSCoordinatesProps {
    latitude?: number;
    longitude?: number;
    accuracy?: number;
    required?: boolean;
    accuracyThreshold?: number;
    disabled?: boolean;
    onCoordinatesChange?: (coordinates: {
        latitude: number;
        longitude: number;
        accuracy: number;
    }) => void;
}

export const GPSCoordinates: React.FC<GPSCoordinatesProps> = ({
    latitude,
    longitude,
    accuracy,
    required = false,
    accuracyThreshold = 50,
    disabled = false,
    onCoordinatesChange
}) => {
    const { dark } = useTheme();
    const [isGettingLocation, setIsGettingLocation] = useState<boolean>(false);
    const [coord, setCoord] = useState({
        latitude: latitude || 0,
        longitude: longitude || 0,
        accuracy: accuracy || 0
    });

    // Update internal state when props change
    useEffect(() => {
        if (latitude !== undefined && longitude !== undefined) {
            setCoord({
                latitude: latitude,
                longitude: longitude,
                accuracy: accuracy || 0
            });
        }
    }, [latitude, longitude, accuracy]);

    const handleGetCurrentLocation = () => {
        if (!navigator.geolocation) {
            showErrorNotification('Error', 'Geolocation is not supported by your browser', dark);
            return;
        }

        setIsGettingLocation(true);

        navigator.geolocation.getCurrentPosition(
            (position) => {
                const newCoordinates = {
                    latitude: position.coords.latitude,
                    longitude: position.coords.longitude,
                    accuracy: position.coords.accuracy
                };

                setCoord(newCoordinates);
                if (onCoordinatesChange) {
                    onCoordinatesChange(newCoordinates);
                }
                setIsGettingLocation(false);
            },
            (error) => {
                console.error('Error getting location:', error);
                let errorMessage = 'Failed to get your location';
                
                switch (error.code) {
                    case error.PERMISSION_DENIED:
                        errorMessage = 'Location permission denied. Please enable location services.';
                        break;
                    case error.POSITION_UNAVAILABLE:
                        errorMessage = 'Location information is unavailable.';
                        break;
                    case error.TIMEOUT:
                        errorMessage = 'Location request timed out.';
                        break;
                }
                
                showErrorNotification('Error', errorMessage, dark);
                setIsGettingLocation(false);
            },
            { enableHighAccuracy: true }
        );
    };

    const handleCoordinateChange = (field: keyof typeof coord, value: string) => {
        // Convert to number or default to 0 if empty
        const numValue = value !== '' ? parseFloat(value) : 0;
        
        const newCoordinates = {
            ...coord,
            [field]: numValue
        };
        
        setCoord(newCoordinates);
        
        if (onCoordinatesChange) {
            onCoordinatesChange(newCoordinates);
        }
    };

    // Determine if accuracy is good (below threshold)
    const isAccuracyGood = coord.accuracy > 0 && coord.accuracy <= accuracyThreshold;
    
    // Determine badge status for accuracy
    const getAccuracyBadgeStatus = () => {
        if (coord.accuracy === 0) return 'default';
        return isAccuracyGood ? 'success' : 'error';
    };

    // Format coordinates to 6 decimal places for display
    const formatCoordinate = (value: number) => {
        return value ? value.toFixed(6) : '0.000000';
    };

    return (
        <Card 
            title={
                <Space>
                    <EnvironmentOutlined />
                    <span>GPS Coordinates</span>
                    {required && <Text type="danger">*</Text>}
                </Space>
            }
            size="small"
            className="mb-4"
        >
            <Space direction="vertical" className="w-full">
                <Form.Item label="Latitude" className="mb-2">
                    <Input
                        value={formatCoordinate(coord.latitude)}
                        onChange={(e) => handleCoordinateChange('latitude', e.target.value)}
                        disabled={disabled}
                        addonBefore="Lat"
                        placeholder="0.000000"
                        type="number"
                        step="0.000001"
                    />
                </Form.Item>
                
                <Form.Item label="Longitude" className="mb-2">
                    <Input
                        value={formatCoordinate(coord.longitude)}
                        onChange={(e) => handleCoordinateChange('longitude', e.target.value)}
                        disabled={disabled}
                        addonBefore="Long"
                        placeholder="0.000000"
                        type="number"
                        step="0.000001"
                    />
                </Form.Item>
                
                <Form.Item label="Accuracy">
                    <Space>
                        <Input
                            value={coord.accuracy ? coord.accuracy.toFixed(1) : '0.0'}
                            disabled={true}
                            addonBefore="Acc"
                            addonAfter="m"
                            style={{ width: '150px' }}
                        />
                        <Badge 
                            status={getAccuracyBadgeStatus()}
                            text={
                                coord.accuracy === 0 
                                    ? 'No data' 
                                    : isAccuracyGood 
                                        ? 'Good' 
                                        : `Poor (>${accuracyThreshold}m)`
                            }
                        />
                    </Space>
                </Form.Item>
                
                <Button
                    type="primary"
                    icon={isGettingLocation ? <LoadingOutlined /> : <AimOutlined />}
                    onClick={handleGetCurrentLocation}
                    disabled={disabled || isGettingLocation}
                    loading={isGettingLocation}
                    block
                >
                    {isGettingLocation ? 'Getting Location...' : 'Get Current Location'}
                </Button>
                
                {coord.latitude !== 0 && coord.longitude !== 0 && (
                    <Button 
                        href={`https://maps.google.com?q=${coord.latitude},${coord.longitude}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-2"
                        block
                    >
                        View on Map
                    </Button>
                )}
            </Space>
        </Card>
    );
};

export default GPSCoordinates;
